import type {
  AdminAuthenticationResponse,
  ChangePasswordRequest,
  UserResponse,
  UserSession,
  UserSessionResponse,
} from "@repo/types"
import type { FetchError } from "ofetch"
import type { AuthResult, Credentials, SessionUser } from "../model/types"
import { apiErrorMessage, http } from "@/shared/api/http"
import { toUser, toUserSession } from "@/shared/api/mappers"

/**
 * Thrown on bad credentials or an unusable token. The message is an i18n key so
 * the copy follows the UI language; `detail` carries a server-provided message
 * when there is one, which the caller should prefer over translating the key.
 */
export class AuthError extends Error {
  readonly detail?: string

  constructor(key = "auth.invalidCredentials", detail?: string) {
    super(key)
    this.name = "AuthError"
    this.detail = detail
  }
}

function toAuthResult(response: AdminAuthenticationResponse): AuthResult {
  const { accessToken, refreshToken, user } = response
  // A 200 without a full token pair means the contract changed under us; failing
  // loudly here beats a half-authenticated session.
  if (!accessToken || !refreshToken || !user)
    throw new AuthError("auth.incompleteResponse")

  return { accessToken, refreshToken, user: toUser(user) }
}

export const authApi = {
  async login(credentials: Credentials): Promise<AuthResult> {
    try {
      const response = await http<AdminAuthenticationResponse>("/Auth/Login", {
        method: "POST",
        body: { email: credentials.email.trim(), password: credentials.password },
        auth: false,
      })
      return toAuthResult(response)
    } catch (error) {
      if (error instanceof AuthError)
        throw error
      // The endpoint answers 400 only for bad credentials, and its ProblemDetails
      // text is English — so that case gets our own copy. Anything else (500,
      // network) keeps the server's wording, which carries real information.
      if ((error as FetchError)?.response?.status === 400)
        throw new AuthError()
      throw new AuthError("auth.failed", apiErrorMessage(error))
    }
  },

  /** Current admin with roles and flattened permissions. */
  async me(): Promise<SessionUser> {
    return toUser(await http<UserResponse>("/Auth"))
  },

  async logout(refreshToken: string): Promise<void> {
    await http<void>("/Auth/Logout", { method: "POST", body: { refreshToken } })
  },

  async changePassword(body: ChangePasswordRequest): Promise<void> {
    await http<void>("/Auth/ChangePassword", { method: "POST", body })
  },

  async listSessions(): Promise<UserSession[]> {
    const response = await http<UserSessionResponse[]>("/Auth/ListSessions")
    return response.map(toUserSession)
  },

  async revokeSession(sessionId: string): Promise<void> {
    await http<void>(`/Auth/RevokeSession/${sessionId}`, { method: "DELETE" })
  },

  /** Signs out every device except the one holding this refresh token. */
  async revokeOtherSessions(refreshToken: string): Promise<void> {
    await http<void>("/Auth/RevokeOtherSessions", { method: "POST", body: { refreshToken } })
  },
}
